import React, { useEffect, useState, useCallback } from 'react';
import { getActiveAd, recordImpression, recordClick } from '../api/ad';
import type { AdData } from '../api/ad';
import { useLanguage } from '../i18n/LanguageContext';
import styles from './AdBanner.module.css';

interface AdBannerProps {
  storeId: string;
}

/**
 * 광고 배너 컴포넌트
 * - storeId로 활성 광고를 조회하여 표시
 * - 광고 없으면 렌더링하지 않음
 * - 표시 시 노출 카운트 기록
 * - 클릭 시 클릭 카운트 기록 + 새 탭에서 linkUrl 열기
 */
export function AdBanner({ storeId }: AdBannerProps) {
  const { t } = useLanguage();
  const [ad, setAd] = useState<AdData | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchAd() {
      const result = await getActiveAd(storeId);
      if (!cancelled && result) {
        setAd(result);
        // 노출 기록
        recordImpression(result.id);
      }
    }

    fetchAd();

    return () => {
      cancelled = true;
    };
  }, [storeId]);

  const handleClick = useCallback(() => {
    if (!ad) return;

    // 클릭 기록
    recordClick(ad.id);

    // 새 탭에서 linkUrl 열기
    if (ad.linkUrl) {
      window.open(ad.linkUrl, '_blank', 'noopener,noreferrer');
    }
  }, [ad]);

  if (!ad) {
    return (
      <div className={styles.container}>
        <div className={styles.placeholder} aria-label="배너 자리">
          <span className={styles.placeholderText}>배너자리</span>
          <span className={styles.placeholderHint}>주변 카페 · 미용실 · 네일아트</span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div
        className={styles.banner}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        aria-label={`${ad.advertiserName} ${t('ad_label')}`}
      >
        <img
          className={styles.bannerImage}
          src={ad.imageUrl}
          alt={`${ad.advertiserName} ${t('ad_label')}`}
        />
        <span className={styles.adLabel}>{t('ad_label')}</span>
      </div>
    </div>
  );
}
